import { useInfiniteQuery } from "@tanstack/react-query";

import axiosServices from "../utils/axios";

import type { JobList } from "../utils/types/voyages";

interface FetchJobsApi {
  data: JobList[];
  total: number;
}
export const fetchJobsData = async (
  page: number,
  pageSize: number | null,
  version?: string,
  statusValue?: string,
  q?: string,
  sort?: {
    field: string;
    direction: string;
  },
  agentId?: string
) => {
  const params: any = {
    format: "berthdata",
    page: page + 1,
    sort: sort
      ? JSON.stringify([{ property: sort.field, direction: sort.direction }])
      : '[{"property":"billing_reference","direction":"ASC"}]',
  };

  if (pageSize) {
    params.start = page * pageSize;
    params.limit = pageSize;
  }

  // Only add version to params if it exists
  if (version) {
    params.version = version;
  }
  if (statusValue) {
    params.statusValue = statusValue;
  }
  if (q) {
    params.q = q;
  }
  if (agentId) {
    params.agent_id = agentId; // Common convention for user ID filtering
  }
  const response = await axiosServices.get("/jobs", {
    params,
  });
  return response.data;
};

export const getJobsData = async (version?: string) => {
  const params: Record<string, any> = {
    version: "all",
    page: 1,
    start: 0,
    limit: 25,
  };
  if (version) {
    params.version = version;
  }
  const response = await axiosServices.get<FetchJobsApi>("/jobs", {
    params,
  });
  return response.data;
};

export const getJobsList = async () => {
  const params: Record<string, any> = {
    version: "active",
    page: 1,
    start: 0,
    limit: 50,
    format: "berthdata",
    sort: JSON.stringify([
      {
        property: "billing_reference",
        direction: "ASC",
      },
    ]),
  };

  const response = await axiosServices.get("/jobs", { params });
  return response.data;
};

export const getJobsListWithBerthData = async (
  page: number,
  pageSize: number | null,
  version?: string,
  statusValue?: string,
  q?: string,
  sort?: {
    field: string;
    direction: string;
  },
  agentId?: string
) => {
  const params: any = {
    format: "berthdata",
    page:page+1,
    sort: sort
      ? JSON.stringify([{ property: sort.field, direction: sort.direction }])
      : '[{"property":"assigned_agent","direction":"ASC"}]',
  };

  if (pageSize) {
    params.start = page * pageSize;
    params.limit = pageSize;
  }

  // Only add version to params if it exists
  if (version) {
    params.version = version;
  }
  if (statusValue) {
    params.statusValue = statusValue;
  }
  if (q) {
    params.q = q;
  }
  if (agentId) {
    params.agent_id = agentId; // Common convention for user ID filtering
  }

  const response = await axiosServices.get("/jobs", { params });
  return response.data;
};


export const fetchJobEmails = async (
  jobId: string,
  params?: Record<string, any>
) => {
  if (!jobId) {
    throw new Error("jobId is required for fetchJobEmails");
  }

  const defaultParams: Record<string, any> = {
    top: 250,
    folderId:
      "AAMkADkxMjM5N2M3LTMwMDAtNDM1ZS1iNGZhLTU3ZGVhYzc5OWM4OAAuAAAAAADVu4-eHO76T7n76_FjPqwwAQBnwV_kPT9STL4URgrR5wDVAAAAAAEMAAA%3D",
    filter: "not(contains(from%2FemailAddress%2Faddress,%27%40tma-usgulf.com%27))",
    $skip: 0,
  };

  const merged = { ...defaultParams, ...(params || {}) };

  const response = await axiosServices.get(`/outlook/jobs/${encodeURIComponent(jobId)}/emails`, {
    params: merged,
  });

  return response.data;
};

export const useJobEmails = (jobId: string | null, enabled: boolean = true) =>
  useInfiniteQuery({
    queryKey: ["jobEmails", jobId],
    queryFn: async ({ pageParam = 0 }) => {
      if (!jobId) return [];
      const top = 250;
      const skip = pageParam * top;
      const data = await fetchJobEmails(jobId, { top, $skip: skip });
      return data.data || [];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // If API returns an array and it's less than page size, assume no more pages.
      if (!lastPage || (Array.isArray(lastPage) && lastPage.length < 250)) {
        return undefined;
      }
      return allPages.length;
    },
    enabled: enabled && !!jobId,
  });
